import {
  useListTemplates,
  useListTrashedTemplates,
  useDeleteTemplate,
  useBulkDeleteTemplates,
  useGetMe,
  useGetRepDefaults,
  useRestoreTemplate,
  usePurgeTemplate,
  getListTemplatesQueryKey,
  getListTrashedTemplatesQueryKey,
  type Template,
} from "@workspace/api-client-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Loader2,
  Plus,
  FileCode2,
  Trash2,
  Info,
  X,
  CheckSquare,
  RotateCcw,
  Sparkles,
} from "lucide-react";
import { Link } from "wouter";
import { useMemo, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { isAutoSuffixedSlug } from "@/lib/templateSlug";
import { rewriteLinksForPreview } from "@/lib/previewHtml";
import { getTrashCountdown } from "@/lib/trashCountdown";

// Same rep-default keys honored by Compose and the template detail
// preview. Using the saved Representative name and phone here means
// the card thumbnails reflect the user's actual identity (e.g.
// "Bruce Kane" / their direct callback number) instead of the seeded
// samples (e.g. "Alex Rivera" / "+1 (800) 555-0188") wherever a
// template uses `{{agentName}}`, `{{repName}}`, or `{{repPhone}}`.
function buildPreviewSrcDoc(
  template: Template,
  repName: string | null | undefined,
  repPhone: string | null | undefined,
): string {
  const repFallbacks: Record<string, string | null | undefined> = {
    repName,
    agentName: repName,
    repPhone,
  };
  let html = template.bodyHtml;
  for (const v of template.variables) {
    const repVal = repFallbacks[v.key];
    const val =
      typeof repVal === "string" && repVal !== ""
        ? repVal
        : (v.defaultValue ?? `[${v.label || v.key}]`);
    const escaped = val
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    const re = new RegExp(`\\{\\{\\s*${v.key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*\\}\\}`, "g");
    html = html.replace(re, escaped);
  }
  return rewriteLinksForPreview(html.replace(/\{\{\s*[\w.]+\s*\}\}/g, ""));
}

function TemplateThumb({
  template,
  repName,
  repPhone,
}: {
  template: Template;
  repName: string | null | undefined;
  repPhone: string | null | undefined;
}) {
  const srcDoc = useMemo(
    () => buildPreviewSrcDoc(template, repName, repPhone),
    [template, repName, repPhone],
  );
  return (
    <div className="relative h-40 w-full overflow-hidden bg-muted/40 border-b border-border">
      <div
        className="absolute top-0 left-0 origin-top-left pointer-events-none"
        style={{ width: "250%", height: "250%", transform: "scale(0.4)" }}
      >
        <iframe
          title={`Preview of ${template.name}`}
          srcDoc={srcDoc}
          sandbox="allow-popups allow-popups-to-escape-sandbox"
          className="w-full h-full border-0 bg-white"
          loading="lazy"
        />
      </div>
    </div>
  );
}

export default function Templates() {
  const [view, setView] = useState<"active" | "trash">("active");
  const [source, setSource] = useState<"all" | "builtin" | "user">("all");
  const [category, setCategory] = useState<string>("all");
  const [confirmDelete, setConfirmDelete] = useState<Template | null>(null);
  const [confirmPurge, setConfirmPurge] = useState<Template | null>(null);
  // Multi-select set keyed by template id. Held across pagination/filter
  // changes intentionally — if the user bulk-selects "Welcome" then changes
  // the category filter, "Welcome" is still queued for delete. Cleared on
  // view switch (Active <-> Trash) since the ids aren't comparable.
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);

  const queryClient = useQueryClient();
  const { data: templates, isLoading } = useListTemplates({
    category: category !== "all" ? category : undefined,
    source: source,
  });
  const { data: me } = useGetMe();
  // Saved Representative name — passed down to every TemplateThumb so
  // the card previews substitute the user's chosen rep identity for
  // `{{agentName}}` / `{{repName}}` instead of the seeded sample.
  const { data: repDefaults } = useGetRepDefaults();
  // Always load the Trash list so we can show the badge count even while
  // the Active view is selected. The list is small (one user's deleted
  // templates) and the query is cached, so this is cheap.
  const { data: trashed, isLoading: isTrashLoading } = useListTrashedTemplates();
  // Permission flag — owners can move their own templates to Trash; only
  // admins may hide built-in templates globally.
  const isAdmin = me?.isAdmin === true;
  const deleteMutation = useDeleteTemplate();
  const bulkDeleteMutation = useBulkDeleteTemplates();
  const restoreMutation = useRestoreTemplate();
  const purgeMutation = usePurgeTemplate();

  function invalidateTemplateQueries() {
    // Refresh both the active list (any source/category combo) and the
    // Trash list — every soft-delete / restore / purge moves rows
    // between the two, so both sides need a refetch.
    queryClient.invalidateQueries({
      queryKey: getListTemplatesQueryKey().slice(0, 1),
    });
    queryClient.invalidateQueries({
      queryKey: getListTrashedTemplatesQueryKey(),
    });
  }

  // Restore one template by id and surface success/error. Used both by
  // the explicit Restore button in the Trash view and by the Undo toast
  // shown after a soft-delete in the Active view. Errors are toasted
  // here so callers don't each have to repeat the same handling.
  async function restoreOne(id: number, name: string): Promise<boolean> {
    try {
      await restoreMutation.mutateAsync({ id });
      toast.success(`Restored "${name}"`);
      invalidateTemplateQueries();
      return true;
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Failed to restore template";
      toast.error(msg);
      return false;
    }
  }

  // Restore many templates in parallel. The Undo toast for a bulk
  // delete calls this so the user can revert the whole batch with one
  // click. Counts are tallied for the success toast so the user knows
  // exactly how many came back.
  async function restoreMany(items: Array<{ id: number; name: string }>) {
    if (items.length === 0) return;
    const settled = await Promise.allSettled(
      items.map((i) => restoreMutation.mutateAsync({ id: i.id })),
    );
    const ok = settled.filter((s) => s.status === "fulfilled").length;
    const fail = settled.length - ok;
    if (ok > 0) {
      toast.success(
        fail === 0
          ? `Restored ${ok} ${ok === 1 ? "template" : "templates"}`
          : `Restored ${ok} of ${items.length} (${fail} failed)`,
      );
    } else {
      toast.error("Failed to restore templates");
    }
    invalidateTemplateQueries();
  }

  async function handleConfirmDelete() {
    if (!confirmDelete) return;
    // Snapshot the row BEFORE we mutate so the Undo toast still has a
    // valid id+name even after the dialog is closed and the row is
    // gone from the active list.
    const snapshot = { id: confirmDelete.id, name: confirmDelete.name };
    const isBuiltin = confirmDelete.isBuiltin;
    try {
      await deleteMutation.mutateAsync({ id: snapshot.id });
      if (isBuiltin) {
        toast.success(`"${snapshot.name}" hidden from all users`);
      } else {
        toast.success(`Moved "${snapshot.name}" to Trash`, {
          action: {
            label: "Undo",
            onClick: () => {
              void restoreOne(snapshot.id, snapshot.name);
            },
          },
        });
      }
      // Drop the just-deleted id from the selection set so the bulk
      // toolbar's count stays in sync.
      setSelectedIds((prev) => {
        if (!prev.has(snapshot.id)) return prev;
        const next = new Set(prev);
        next.delete(snapshot.id);
        return next;
      });
      setConfirmDelete(null);
      invalidateTemplateQueries();
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Failed to delete template";
      toast.error(msg);
    }
  }

  async function handleConfirmBulk() {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    // Snapshot the names of each selected id so we can attach a useful
    // Undo handler regardless of how the active list changes.
    const byId = new Map((templates ?? []).map((t) => [t.id, t]));
    try {
      const result = await bulkDeleteMutation.mutateAsync({ data: { ids } });
      const { deleted, hidden, not_found, forbidden } = result.counts;
      const parts: string[] = [];
      // "deleted" here means "moved to Trash" — surface that wording so
      // the user understands it's reversible.
      if (deleted) parts.push(`${deleted} moved to Trash`);
      if (hidden) parts.push(`${hidden} hidden`);
      if (not_found) parts.push(`${not_found} already gone`);
      if (forbidden) parts.push(`${forbidden} not permitted`);

      // Only the soft-deleted user templates can be undone. Built-in
      // hides are an admin/global action and aren't reversible from
      // this UI.
      const undoable = result.results
        .filter((r) => r.outcome === "deleted")
        .map((r) => {
          const t = byId.get(r.id);
          return { id: r.id, name: t?.name ?? `Template #${r.id}` };
        });

      const toastOptions =
        undoable.length > 0
          ? {
              action: {
                label: "Undo",
                onClick: () => {
                  void restoreMany(undoable);
                },
              },
            }
          : undefined;
      const anyRemoved = (deleted ?? 0) + (hidden ?? 0) > 0;
      if (forbidden && !anyRemoved) {
        toast.error(parts.join(", ") || "No templates were removed — permission denied");
      } else {
        toast.success(parts.join(", ") || `Moved ${ids.length} to Trash`, toastOptions);
      }
      setSelectedIds(new Set());
      setConfirmBulk(false);
      invalidateTemplateQueries();
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Failed to delete templates";
      toast.error(msg);
    }
  }

  async function handleConfirmPurge() {
    if (!confirmPurge) return;
    const snapshot = { id: confirmPurge.id, name: confirmPurge.name };
    try {
      await purgeMutation.mutateAsync({ id: snapshot.id });
      toast.success(`Permanently deleted "${snapshot.name}"`);
      setConfirmPurge(null);
      invalidateTemplateQueries();
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Failed to delete template";
      toast.error(msg);
    }
  }

  function toggleSelect(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAllVisible() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const t of templates ?? []) {
        // Non-admins cannot delete built-in templates; exclude them from
        // "Select all" so the selection only contains actionable items.
        if (t.isBuiltin && !isAdmin) continue;
        next.add(t.id);
      }
      return next;
    });
  }

  function clearSelection() {
    setSelectedIds(new Set());
  }

  function switchView(next: "active" | "trash") {
    if (next === view) return;
    // Drop selection on view switch — the ids and the available actions
    // are different on each side.
    setSelectedIds(new Set());
    setView(next);
  }

  const categories = Array.from(new Set((templates || []).map(t => t.category))).sort();
  // Only templates that the current user is allowed to delete are
  // considered selectable. Non-admins cannot delete built-in templates.
  const selectableIds = (templates ?? [])
    .filter((t) => !t.isBuiltin || isAdmin)
    .map((t) => t.id);
  const allVisibleSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));
  const selectedCount = selectedIds.size;
  const trashCount = trashed?.length ?? 0;

  return (
    <div className="flex-1 overflow-auto p-8 bg-background">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Templates</h1>
            <p className="text-muted-foreground">Manage your email designs.</p>
          </div>
          <Button asChild>
            <Link href="/app/templates/new">
              <Plus className="w-4 h-4 mr-2" />
              New Template
            </Link>
          </Button>
        </div>

        {/* Active / Trash segmented switch. Trash badge always shows the
            current count so the user can see when something is recoverable
            even while browsing the active list. */}
        <div className="inline-flex items-center bg-card border border-border rounded-lg p-1 gap-1">
          <Button
            type="button"
            variant={view === "active" ? "default" : "ghost"}
            size="sm"
            onClick={() => switchView("active")}
            data-testid="button-view-active"
          >
            Templates
          </Button>
          <Button
            type="button"
            variant={view === "trash" ? "default" : "ghost"}
            size="sm"
            onClick={() => switchView("trash")}
            data-testid="button-view-trash"
            className="gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Trash
            {trashCount > 0 && (
              <Badge
                variant="secondary"
                className="ml-1 h-5 px-1.5 text-[10px]"
                data-testid="badge-trash-count"
              >
                {trashCount}
              </Badge>
            )}
          </Button>
        </div>

        {view === "active" ? (
          <>
            <div className="flex items-center gap-4 bg-card border border-border p-2 rounded-lg">
              <Select value={source} onValueChange={(v) => setSource(v as typeof source)}>
                <SelectTrigger className="w-[180px] border-none shadow-none focus:ring-0">
                  <SelectValue placeholder="Source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  <SelectItem value="builtin">Built-in</SelectItem>
                  <SelectItem value="user">My Templates</SelectItem>
                </SelectContent>
              </Select>
              <div className="w-px h-6 bg-border mx-2" />
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="w-[180px] border-none shadow-none focus:ring-0">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="ml-auto flex items-center gap-2">
                {selectableIds.length > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={allVisibleSelected ? clearSelection : selectAllVisible}
                    data-testid="button-toggle-select-all"
                  >
                    <CheckSquare className="w-4 h-4 mr-2" />
                    {allVisibleSelected ? "Clear selection" : "Select all"}
                  </Button>
                )}
              </div>
            </div>

            {selectedCount > 0 && (
              <div
                className="flex items-center justify-between gap-3 bg-primary/5 border border-primary/20 px-4 py-2.5 rounded-lg"
                data-testid="bulk-action-bar"
              >
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium">
                    {selectedCount} selected
                  </span>
                  <span className="text-muted-foreground hidden sm:inline">
                    {isAdmin
                      ? "— your templates go to Trash; built-ins are hidden globally"
                      : "— selected templates will be moved to Trash"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearSelection}
                    data-testid="button-clear-selection"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Clear
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => setConfirmBulk(true)}
                    data-testid="button-bulk-delete"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Move {selectedCount} to Trash
                  </Button>
                </div>
              </div>
            )}

            {isLoading ? (
              <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
            ) : templates?.length === 0 ? (
              <div className="text-center py-20 px-4 border-2 border-dashed border-border rounded-xl bg-card">
                <FileCode2 className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-medium mb-2">No templates found</h3>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  You haven't created any templates yet. Start from scratch or generate one with AI.
                </p>
                <Button asChild>
                  <Link href="/app/templates/new">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate with AI
                  </Link>
                </Button>
              </div>
            ) : (
              <TooltipProvider delayDuration={150}>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {templates?.map((template, i) => {
                  const slugAdjusted =
                    !template.isBuiltin &&
                    isAutoSuffixedSlug(template.name, template.slug);
                  return (
                  <Link key={template.id} href={`/app/templates/${template.id}`}>
                    <Card
                      className={`h-full cursor-pointer hover:border-primary/50 transition-colors flex flex-col group animate-in fade-in zoom-in duration-500 fill-mode-both overflow-hidden relative ${
                        selectedIds.has(template.id) ? "ring-2 ring-primary border-primary" : ""
                      }`}
                      style={{ animationDelay: `${i * 50}ms` }}
                    >
                      {(!template.isBuiltin || isAdmin) && (
                        <div
                          className={`absolute top-2 left-2 z-10 transition-opacity ${
                            selectedIds.has(template.id)
                              ? "opacity-100"
                              : "opacity-0 group-hover:opacity-100"
                          }`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <Checkbox
                            checked={selectedIds.has(template.id)}
                            onCheckedChange={() => toggleSelect(template.id)}
                            aria-label={`Select ${template.name}`}
                            data-testid={`checkbox-select-template-${template.id}`}
                            className="bg-background/95 border-border shadow-sm"
                          />
                        </div>
                      )}
                      {(!template.isBuiltin || isAdmin) && (
                        <Button
                          type="button"
                          size="icon"
                          variant="secondary"
                          aria-label={
                            template.isBuiltin
                              ? `Hide built-in template ${template.name} from all users`
                              : `Move ${template.name} to Trash`
                          }
                          data-testid={`button-delete-template-${template.id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setConfirmDelete(template);
                          }}
                          className="absolute top-2 right-2 z-10 h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity bg-background/95 hover:bg-destructive hover:text-destructive-foreground shadow-sm"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                      <TemplateThumb
                        template={template}
                        repName={repDefaults?.repName ?? null}
                        repPhone={repDefaults?.repPhone ?? null}
                      />
                      <CardHeader className="pb-3">
                        <div className="flex justify-between items-start mb-2">
                          <Badge variant={template.isBuiltin ? "secondary" : "default"} className="font-medium text-[10px] uppercase tracking-wider">
                            {template.category}
                          </Badge>
                          {template.isBuiltin && (
                            <Badge variant="outline" className="text-[10px] uppercase tracking-wider bg-muted/50">Built-in</Badge>
                          )}
                        </div>
                        <CardTitle className="text-lg line-clamp-1 group-hover:text-primary transition-colors flex items-center gap-1.5">
                          <span className="line-clamp-1">{template.name}</span>
                          {slugAdjusted && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  role="button"
                                  tabIndex={0}
                                  aria-label="URL was adjusted to avoid a conflict"
                                  data-testid={`slug-auto-suffix-indicator-${template.id}`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }
                                  }}
                                  className="inline-flex items-center justify-center rounded text-blue-600 dark:text-blue-300 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
                                >
                                  <Info className="w-3.5 h-3.5" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-[260px] bg-popover text-popover-foreground border border-border">
                                <p className="font-medium">URL was adjusted to avoid a conflict.</p>
                                <p className="opacity-80 mt-1">
                                  This template's URL ends in{" "}
                                  <code className="font-mono text-[11px]">
                                    {template.slug}
                                  </code>{" "}
                                  because the name you chose is reserved or already
                                  in use.
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </CardTitle>
                        <CardDescription className="line-clamp-2 min-h-[2.5rem]">
                          {template.description}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="flex-1 pb-4">
                        <div className="text-sm font-medium text-foreground mb-1 line-clamp-1">
                          {template.subject}
                        </div>
                        {template.previewText && (
                          <div className="text-xs text-muted-foreground line-clamp-1">
                            {template.previewText}
                          </div>
                        )}
                      </CardContent>
                      <CardFooter className="pt-0 pb-4 text-xs text-muted-foreground flex justify-between border-t border-border mt-auto pt-4">
                        <span>{template.variables.length} variables</span>
                        <span>{formatDistanceToNow(new Date(template.createdAt), { addSuffix: true })}</span>
                      </CardFooter>
                    </Card>
                  </Link>
                  );
                })}
              </div>
              </TooltipProvider>
            )}
          </>
        ) : (
          /* Trash view: same card grid, but with Restore + Delete-forever
             actions and no checkboxes / bulk toolbar / source filter
             (everything in here is a user-owned template). */
          <>
            <p
              className="text-xs text-muted-foreground"
              data-testid="trash-auto-purge-hint"
            >
              Items are auto-deleted after 30 days.
            </p>
            {isTrashLoading ? (
              <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
            ) : trashCount === 0 ? (
              <div
                className="text-center py-20 px-4 border-2 border-dashed border-border rounded-xl bg-card"
                data-testid="trash-empty-state"
              >
                <Trash2 className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-medium mb-2">Trash is empty</h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  Templates you delete will appear here, where you can restore
                  them or delete them permanently.
                </p>
              </div>
            ) : (
              <div
                className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
                data-testid="trash-grid"
              >
                {(trashed ?? []).map((template, i) => (
                  <Card
                    key={template.id}
                    data-testid={`trash-card-${template.id}`}
                    className="h-full flex flex-col animate-in fade-in zoom-in duration-500 fill-mode-both overflow-hidden relative opacity-90"
                    style={{ animationDelay: `${i * 50}ms` }}
                  >
                    <TemplateThumb
                      template={template}
                      repName={repDefaults?.repName ?? null}
                      repPhone={repDefaults?.repPhone ?? null}
                    />
                    <CardHeader className="pb-3">
                      <div className="flex justify-between items-start mb-2">
                        <Badge variant="default" className="font-medium text-[10px] uppercase tracking-wider">
                          {template.category}
                        </Badge>
                        <Badge variant="outline" className="text-[10px] uppercase tracking-wider bg-muted/50">
                          In Trash
                        </Badge>
                      </div>
                      <CardTitle className="text-lg line-clamp-1">
                        {template.name}
                      </CardTitle>
                      <CardDescription className="line-clamp-2 min-h-[2.5rem]">
                        {template.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex-1 pb-4">
                      <div className="text-sm font-medium text-foreground mb-1 line-clamp-1">
                        {template.subject}
                      </div>
                      {template.deletedAt && (
                        <div className="text-xs text-muted-foreground">
                          Deleted{" "}
                          {formatDistanceToNow(new Date(template.deletedAt), {
                            addSuffix: true,
                          })}
                        </div>
                      )}
                      {(() => {
                        // Per-row countdown: only renders inside the
                        // last `TRASH_COUNTDOWN_VISIBLE_DAYS` of the
                        // retention window so we don't badge every
                        // freshly trashed item with a deadline.
                        const countdown = getTrashCountdown(template.deletedAt);
                        if (!countdown) return null;
                        return (
                          <div
                            data-testid={`trash-countdown-${template.id}`}
                            data-urgent={countdown.urgent ? "true" : "false"}
                            className={
                              countdown.urgent
                                ? "text-xs font-medium text-amber-600 dark:text-amber-400 mt-1"
                                : "text-xs text-muted-foreground mt-1"
                            }
                          >
                            {countdown.daysLeft === 1
                              ? "Deletes in 1 day"
                              : `Deletes in ${countdown.daysLeft} days`}
                          </div>
                        );
                      })()}
                    </CardContent>
                    <CardFooter className="pt-4 border-t border-border mt-auto flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="default"
                        className="flex-1"
                        data-testid={`button-restore-template-${template.id}`}
                        disabled={restoreMutation.isPending}
                        onClick={() => {
                          void restoreOne(template.id, template.name);
                        }}
                      >
                        <RotateCcw className="w-4 h-4 mr-1" />
                        Restore
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        className="flex-1"
                        data-testid={`button-purge-template-${template.id}`}
                        onClick={() => setConfirmPurge(template)}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete forever
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <AlertDialog
        open={confirmBulk}
        onOpenChange={(o) => {
          if (!o && !bulkDeleteMutation.isPending) setConfirmBulk(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Move {selectedCount}{" "}
              {selectedCount === 1 ? "template" : "templates"} to Trash?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isAdmin
                ? "Your templates will be moved to Trash and can be restored. Built-in templates will be hidden from every account on this workspace."
                : "User templates in your selection will be moved to the Trash and can be restored later."}{" "}
              Sent emails keep their saved copy in Logs.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleteMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                // Radix closes the dialog by default — prevent that so the
                // pending spinner stays visible until the mutation settles.
                e.preventDefault();
                void handleConfirmBulk();
              }}
              disabled={bulkDeleteMutation.isPending}
              data-testid="button-confirm-bulk-delete"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {bulkDeleteMutation.isPending && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              Move {selectedCount} to Trash
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDelete?.isBuiltin
                ? "Hide this built-in template?"
                : "Move this template to Trash?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.isBuiltin
                ? `"${confirmDelete?.name}" will be hidden from all users across this workspace. This cannot be reversed from the Trash. Sent emails keep their saved copy in Logs.`
                : `"${confirmDelete?.name}" will be moved to the Trash and hidden from your templates list. You can restore it from the Trash, or delete it permanently from there. Sent emails keep their saved copy in Logs.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
              data-testid="button-confirm-delete-template"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              <Trash2 className="w-4 h-4 mr-1" />
              {confirmDelete?.isBuiltin ? "Hide from all users" : "Move to Trash"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!confirmPurge}
        onOpenChange={(o) => {
          if (!o && !purgeMutation.isPending) setConfirmPurge(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete forever?</AlertDialogTitle>
            <AlertDialogDescription>
              "{confirmPurge?.name}" will be permanently deleted and cannot be
              restored. Sent emails that used this template keep their saved
              copy in Logs.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={purgeMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleConfirmPurge();
              }}
              disabled={purgeMutation.isPending}
              data-testid="button-confirm-purge-template"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {purgeMutation.isPending && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              <Trash2 className="w-4 h-4 mr-1" />
              Delete forever
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}
